import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fmt, type ReportsPayload } from '../../lib/api';
import type { Drill } from '../../pages/Reports';
import { COLORS } from './utils';

/**
 * Cross-dimensional drill-down detail. Reacts to the page's `drill` selection:
 *   - agent selected   → that agent's earnings split BY CARRIER over time
 *   - carrier selected → that carrier's earnings split BY AGENT over time
 * Replaces the old agent×carrier matrix grid with a focused master→detail view.
 * All values come from `matrix` (earned only — carry-forward is agent-level).
 */
export default function DetailPanel({
  data,
  drill,
  setDrill,
}: {
  data: ReportsPayload;
  drill: Drill;
  setDrill: (d: Drill) => void;
}) {
  const detail = useMemo(() => {
    if (!drill) return null;
    if (drill.kind === 'agent') {
      const name = data.agents.find((a) => a.agent_id === drill.id)?.agent_name ?? drill.id;
      const keys = data.carriers.filter((c) =>
        data.periods.some((p) => (data.matrix[p.pay_run_id]?.[drill.id]?.[c] ?? 0) !== 0),
      );
      const valAt = (rid: string, key: string) => data.matrix[rid]?.[drill.id]?.[key] ?? 0;
      return { title: `${name} — by carrier`, label: 'Carrier', keys, valAt };
    }
    const nameToId = new Map(data.agents.map((a) => [a.agent_name, a.agent_id]));
    const keys = data.agents
      .filter((a) => data.periods.some((p) => (data.matrix[p.pay_run_id]?.[a.agent_id]?.[drill.id] ?? 0) !== 0))
      .map((a) => a.agent_name);
    const valAt = (rid: string, key: string) => data.matrix[rid]?.[nameToId.get(key) ?? '']?.[drill.id] ?? 0;
    return { title: `${drill.id} — by agent`, label: 'Agent', keys, valAt };
  }, [data, drill]);

  if (!drill || !detail) {
    return (
      <section className="card p-4 text-center">
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Click an agent or carrier above to break it down over time.
        </p>
      </section>
    );
  }

  const { title, label, keys, valAt } = detail;
  const chartData = data.periods.map((p) => {
    const row: Record<string, number | string> = { period: p.period_label };
    for (const k of keys) row[k] = valAt(p.pay_run_id, k);
    return row;
  });
  const lastTwo = data.periods.slice(-2);

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-medium">{title}</h2>
        <button
          className="text-xs underline"
          style={{ color: 'var(--color-text-muted)' }}
          onClick={() => setDrill(null)}
        >
          clear
        </button>
      </div>

      {keys.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          No earnings to break down for this selection.
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmt.money(v)} />
              <Tooltip formatter={(v) => (typeof v === 'number' ? fmt.money(v) : String(v ?? ''))} />
              <Legend />
              {keys.map((k, i) => (
                <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>

          <div className="overflow-x-auto mt-3">
            <table className="table w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-table-header)' }}>
                  <th className="py-1 text-left">{label}</th>
                  {data.periods.map((p) => (
                    <th key={p.pay_run_id} className="py-1 text-right">
                      {p.period_label}
                    </th>
                  ))}
                  <th className="py-1 text-right">Δ vs prior</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const prev = lastTwo.length === 2 ? valAt(lastTwo[0].pay_run_id, k) : null;
                  const cur = lastTwo.length ? valAt(lastTwo[lastTwo.length - 1].pay_run_id, k) : 0;
                  const delta = prev === null ? null : cur - prev;
                  return (
                    <tr key={k} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td className="py-1">{k}</td>
                      {data.periods.map((p) => (
                        <td key={p.pay_run_id} className="py-1 text-right font-mono">
                          {fmt.money(valAt(p.pay_run_id, k))}
                        </td>
                      ))}
                      <td
                        className="py-1 text-right font-mono"
                        style={
                          delta === null
                            ? { color: 'var(--color-text-muted)' }
                            : delta >= 0
                            ? { color: 'var(--color-success, #16a34a)' }
                            : { color: 'var(--color-danger, #dc2626)' }
                        }
                      >
                        {delta === null ? '—' : (delta >= 0 ? '+' : '') + fmt.money(delta)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
